/**
 * Un contrat d'entretien (2026-10-30 ; plusieurs par site depuis le 2026-11-03) : son nom, sa date de signature (l'année du
 * contrat en part), le rythme des visites de contrôle, et les machines couvertes — d'un ou de plusieurs sites du même client —
 * avec leurs heures vendues par an et la base du calcul. Une machine déjà dans un autre contrat en cours se montre, mais ne se
 * coche pas. Enregistré d'un bloc, avec le jeton de concurrence du contrat : une modification faite entre-temps est refusée,
 * jamais écrasée. Un second panneau termine un contrat (date de fin) : il reste en historique.
 */
import { useState } from 'react';

import { ApiError, api, type ContratsDuSite, type ContratVue } from '../api/client';
import { jourFr } from '../domaine/planning';
import {
  BASES,
  corpsContrat,
  formulaireDepuisContrat,
  lireDateFin,
  machinesDesAutresSites,
  machinesDuPanneau,
  nomMachineSite,
  phraseMachines,
  type FormulaireContrat,
} from '../domaine/preparer-site';
import { ErreurFormulaire, Panneau } from './Formulaire';

export function ContratEntretienPanneau({
  vue,
  contrat,
  aujourdhui,
  onFermer,
  onEnregistre,
}: {
  vue: ContratsDuSite;
  /** Le contrat corrigé, ou nul pour en créer un nouveau depuis ce site. */
  contrat: ContratVue | null;
  aujourdhui: string;
  onFermer: () => void;
  onEnregistre: () => Promise<void>;
}) {
  const [f, setF] = useState<FormulaireContrat>(() => formulaireDepuisContrat(vue, contrat, aujourdhui));
  const [autresSites, setAutresSites] = useState(false);
  const [erreur, setErreur] = useState<Error | null>(null);
  const [enCours, setEnCours] = useState(false);

  const majMachine = (id: string, change: Partial<FormulaireContrat['machines'][number]>) =>
    setF((x) => ({ ...x, machines: x.machines.map((m) => (m.id === id ? { ...m, ...change } : m)) }));

  const enregistrer = async () => {
    const r = corpsContrat(f, contrat?.revision ?? null);
    if ('erreur' in r) {
      setErreur(new Error(r.erreur));
      return;
    }
    setEnCours(true);
    try {
      if (contrat) await api.modifierContrat(contrat.id, vue.site.id, r.corps);
      else await api.creerContrat(vue.site.id, r.corps);
      await onEnregistre();
      onFermer();
    } catch (e) {
      setErreur(e instanceof Error ? e : new Error('Enregistrement refusé.'));
    } finally {
      setEnCours(false);
    }
  };

  const lignes = machinesDuPanneau(vue, f, autresSites);
  const ailleurs = machinesDesAutresSites(vue);
  const cochees = f.machines.filter((m) => m.couverte && m.prisePar === null).length;

  return (
    <Panneau
      titre={contrat ? `Modifier le contrat « ${contrat.label} »` : 'Nouveau contrat d’entretien'}
      sousTitre={`${vue.site.client ? `${vue.site.client} · ` : ''}${vue.site.nom}`}
      onFermer={onFermer}
      large
      actions={
        <>
          <button type="button" className="secondaire" disabled={enCours} onClick={onFermer}>
            Annuler
          </button>
          <button type="button" disabled={enCours} onClick={() => void enregistrer()}>
            Enregistrer le contrat
          </button>
        </>
      }
    >
      <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
      <label>
        Nom du contrat
        <input
          type="text"
          maxLength={80}
          placeholder="ex. Compresseurs"
          value={f.label}
          disabled={enCours}
          onChange={(e) => setF((x) => ({ ...x, label: e.target.value }))}
        />
        <span className="aide">Un site peut avoir plusieurs contrats : le nom permet de les distinguer.</span>
      </label>
      <label>
        Date de signature
        <input type="date" value={f.signedOn} disabled={enCours} onChange={(e) => setF((x) => ({ ...x, signedOn: e.target.value }))} />
        <span className="aide">L’année du contrat part de cette date, d’anniversaire en anniversaire.</span>
      </label>
      <label>
        Visites de contrôle
        <select value={f.controle} disabled={enCours} onChange={(e) => setF((x) => ({ ...x, controle: e.target.value }))}>
          <option value="">Aucune</option>
          <option value="1">Une par mois</option>
          <option value="2">Tous les 2 mois</option>
          <option value="3">Tous les 3 mois</option>
          <option value="6">Tous les 6 mois</option>
          <option value="12">Une par an</option>
        </select>
        <span className="aide">Des visites à part des entretiens, avec leurs propres bons, même le jour d’un entretien.</span>
      </label>

      <h3>Machines couvertes ({phraseMachines(cochees)})</h3>
      {ailleurs > 0 && (
        <label className="case">
          <input type="checkbox" checked={autresSites} disabled={enCours} onChange={() => setAutresSites((v) => !v)} /> Montrer aussi les machines
          des autres sites du client ({ailleurs})
        </label>
      )}
      {vue.machinesDuClientTronquee && <p className="aide">Le client a plus de 1 000 machines : seules les 1 000 premières sont proposées.</p>}
      {lignes.length === 0 ? (
        <div className="vide">Ce site n’a aucune machine active.</div>
      ) : (
        <div className="tableau-defilant">
          <table className="tableau-ecarts">
            <thead>
              <tr>
                <th>Couverte</th>
                <th>Machine</th>
                <th>Site</th>
                <th>Heures par an</th>
                <th>Base du calcul</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map(({ info, saisie: m }) => {
                const prise = m.prisePar !== null;
                return (
                  <tr key={m.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Couvrir ${nomMachineSite(info)}`}
                        checked={m.couverte && !prise}
                        disabled={enCours || prise}
                        onChange={() => majMachine(m.id, { couverte: !m.couverte })}
                      />
                    </td>
                    <td>
                      {nomMachineSite(info)}
                      <div className="aide">
                        {[info.famille, info.numeroSerie ? `n° ${info.numeroSerie}` : null, info.gamme ? info.gamme.label : 'sans gamme'].filter(Boolean).join(' · ')}
                      </div>
                      {prise && <div className="aide attention-texte">Déjà au contrat « {m.prisePar} »</div>}
                    </td>
                    <td>{info.siteId === vue.site.id ? <span className="aide">ce site</span> : info.siteNom}</td>
                    <td>
                      <input
                        type="text"
                        inputMode="numeric"
                        aria-label="Heures par an"
                        placeholder="ex. 3000"
                        value={m.heures}
                        disabled={enCours || !m.couverte || prise}
                        onChange={(e) => majMachine(m.id, { heures: e.target.value })}
                      />
                    </td>
                    <td>
                      <div className="saisie-ligne" role="radiogroup" aria-label="Base du calcul">
                        {BASES.map((b) => (
                          <label key={b.valeur} title={b.aide}>
                            <input
                              type="radio"
                              name={`base-${m.id}`}
                              checked={m.base === b.valeur}
                              disabled={enCours || !m.couverte || prise}
                              onChange={() => majMachine(m.id, { base: b.valeur })}
                            />{' '}
                            {b.libelle}
                          </label>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="aide">
        Visites dues = heures par an ÷ écart entre deux entretiens de la gamme (3 000 h → 1, 8 760 h → 3). « Contrat » : les visites suivent les heures
        vendues ; « Relevés » : elles suivent les heures relevées. Le choix ne change jamais tout seul. Une machine n’est que dans un seul contrat en cours.
      </p>
    </Panneau>
  );
}

/** Terminer un contrat : une date de fin (pas avant la signature, pas dans le futur). Il reste lisible, ne se modifie plus. */
export function TerminerContratPanneau({
  vue,
  contrat,
  aujourdhui,
  onFermer,
  onEnregistre,
}: {
  vue: ContratsDuSite;
  contrat: ContratVue;
  aujourdhui: string;
  onFermer: () => void;
  onEnregistre: () => Promise<void>;
}) {
  const [date, setDate] = useState(aujourdhui);
  const [erreur, setErreur] = useState<Error | null>(null);
  const [enCours, setEnCours] = useState(false);

  const terminer = async () => {
    const r = lireDateFin(date, contrat.signedOn, aujourdhui);
    if ('erreur' in r) {
      setErreur(new Error(r.erreur));
      return;
    }
    setEnCours(true);
    try {
      await api.terminerContrat(contrat.id, vue.site.id, { revision: contrat.revision, endedOn: r.date });
      await onEnregistre();
      onFermer();
    } catch (e) {
      setErreur(e instanceof Error ? e : new Error('Refusé.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre={`Terminer le contrat « ${contrat.label} »`}
      sousTitre={`signé le ${jourFr(contrat.signedOn)} · ${phraseMachines(contrat.machines.length)}`}
      onFermer={onFermer}
      actions={
        <>
          <button type="button" className="secondaire" disabled={enCours} onClick={onFermer}>
            Annuler
          </button>
          <button type="button" disabled={enCours} onClick={() => void terminer()}>
            Terminer le contrat
          </button>
        </>
      }
    >
      <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
      <label>
        Date de fin
        <input type="date" value={date} max={aujourdhui} min={contrat.signedOn} disabled={enCours} onChange={(e) => setDate(e.target.value)} />
      </label>
      <p className="aide">
        Le contrat reste lisible en historique, mais ne se modifie plus. Ses machines redeviennent libres : vous pourrez les mettre dans un nouveau
        contrat. Les interventions déjà créées ne bougent pas.
      </p>
    </Panneau>
  );
}
